/**
 * Utilitaire de gestion de l'ordre des parcs personnalisé par l'utilisateur
 * Sauvegarde et récupère l'ordre préféré dans localStorage
 */

const STORAGE_KEY_PREFIX = 'parkOrder_';

/**
 * Récupère l'ordre personnalisé des parcs pour un continent donné
 * @param {string} continent - Le continent ('europe', 'northAmerica', 'asia')
 * @returns {string[]|null} - Tableau des IDs de parcs dans l'ordre personnalisé, ou null si aucun ordre personnalisé
 */
export const getCustomParkOrder = (continent) => {
  try {
    const storedOrder = localStorage.getItem(`${STORAGE_KEY_PREFIX}${continent}`);
    return storedOrder ? JSON.parse(storedOrder) : null;
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'ordre des parcs:', error);
    return null;
  }
};

/**
 * Sauvegarde l'ordre personnalisé des parcs pour un continent donné
 * @param {string} continent - Le continent ('europe', 'northAmerica', 'asia')
 * @param {string[]} parkIds - Tableau des IDs de parcs dans le nouvel ordre
 */
export const saveCustomParkOrder = (continent, parkIds) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${continent}`, JSON.stringify(parkIds));
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de l\'ordre des parcs:', error);
  }
};

/**
 * Réinitialise l'ordre des parcs à l'ordre par défaut pour un continent donné
 * @param {string} continent - Le continent ('europe', 'northAmerica', 'asia')
 */
export const resetParkOrder = (continent) => {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${continent}`);
  } catch (error) {
    console.error('Erreur lors de la réinitialisation de l\'ordre des parcs:', error);
  }
};

/**
 * Réinitialise l'ordre des parcs pour tous les continents
 */
export const resetAllParkOrders = () => {
  try {
    ['europe', 'northAmerica', 'asia'].forEach(continent => {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${continent}`);
    });
  } catch (error) {
    console.error('Erreur lors de la réinitialisation de tous les ordres de parcs:', error);
  }
};

/**
 * Applique l'ordre personnalisé à une liste de parcs
 * @param {Array} parks - Tableau des objets parcs
 * @param {string} continent - Le continent ('europe', 'northAmerica', 'asia')
 * @returns {Array} - Tableau des parcs réorganisés selon les préférences utilisateur
 */
export const applyCustomOrder = (parks, continent) => {
  const customOrder = getCustomParkOrder(continent);
  
  if (!customOrder || customOrder.length === 0) {
    return parks; // Retourner l'ordre par défaut si aucun ordre personnalisé
  }

  // Créer une map des parcs par ID pour un accès rapide
  const parksMap = new Map(parks.map(park => [park.id, park]));
  
  // Créer le tableau réorganisé en suivant l'ordre personnalisé
  const reorderedParks = [];
  const addedIds = new Set();

  // Ajouter les parcs dans l'ordre personnalisé
  customOrder.forEach(parkId => {
    const park = parksMap.get(parkId);
    if (park) {
      reorderedParks.push(park);
      addedIds.add(parkId);
    }
  });

  // Ajouter les nouveaux parcs qui n'étaient pas dans l'ordre personnalisé à la fin
  parks.forEach(park => {
    if (!addedIds.has(park.id)) {
      reorderedParks.push(park);
    }
  });

  return reorderedParks;
};

/**
 * Déplace un parc vers le haut dans la liste
 * @param {Array} parks - Tableau des objets parcs
 * @param {number} index - Index du parc à déplacer
 * @returns {Array} - Nouveau tableau avec le parc déplacé
 */
export const moveParkUp = (parks, index) => {
  if (index <= 0 || index >= parks.length) return parks;
  
  const newParks = [...parks];
  [newParks[index - 1], newParks[index]] = [newParks[index], newParks[index - 1]];
  return newParks;
};

/**
 * Déplace un parc vers le bas dans la liste
 * @param {Array} parks - Tableau des objets parcs
 * @param {number} index - Index du parc à déplacer
 * @returns {Array} - Nouveau tableau avec le parc déplacé
 */
export const moveParkDown = (parks, index) => {
  if (index < 0 || index >= parks.length - 1) return parks;
  
  const newParks = [...parks];
  [newParks[index], newParks[index + 1]] = [newParks[index + 1], newParks[index]];
  return newParks;
};

/**
 * Vérifie si un ordre personnalisé existe pour un continent donné
 * @param {string} continent - Le continent ('europe', 'northAmerica', 'asia')
 * @returns {boolean} - true si un ordre personnalisé existe
 */
export const hasCustomOrder = (continent) => {
  return getCustomParkOrder(continent) !== null;
};
